import { css } from '@emotion/react';
import { ColDef, CsvExportParams, ExcelExportParams, GridOptions } from 'ag-grid-community';
import { AgGridReact, AgGridReactProps } from 'ag-grid-react';
import { ForwardedRef, forwardRef, memo, useMemo } from 'react';
import { agForesightTheme } from '../../theme/ag-foresight';
import { colors } from '../../theme/colors';
import { useGridTrackingHandlers } from './agTableTracking';
import { customColumnTypes } from './customColumnTypes';
import { customTableNotation } from './customTableNotation';
import { commonProcessCb, getExportParams, getExportToExcelStyles } from './exportToExcelDefs';
import { GridLoadingOverlay } from './GridLoadingOverlay';
import { AG_TABLE_DEFAULT_COL_DEF, AG_TABLE_DEFAULT_FILTER_PARAMS } from './tableConstants';
import { useCloseContextMenuOnBodyScroll } from './useCloseContextMenuOnBodyScroll';

const AgTableForwarded = forwardRef(function AgTable(
  props: AgGridReactProps,
  ref: ForwardedRef<AgGridReact<unknown>>
) {
  const defaultColDef: ColDef = useMemo(
    () => ({
      ...AG_TABLE_DEFAULT_COL_DEF,
      ...(props?.defaultColDef ?? {}),
      filterParams: {
        ...AG_TABLE_DEFAULT_FILTER_PARAMS,
        ...props?.defaultColDef?.filterParams,
      },
    }),
    [props?.defaultColDef]
  );

  const { onGridReady, onFilterChanged } = useGridTrackingHandlers(props);
  const { onBodyScroll, onContextMenuVisibleChanged } = useCloseContextMenuOnBodyScroll(props);

  const excelStyles = getExportToExcelStyles();

  const gridOptions: GridOptions = useMemo(
    () => ({
      ...customTableNotation,
      columnTypes: { ...customColumnTypes },
      excelStyles: excelStyles,
      ...(props?.gridOptions ?? {}),
    }),
    [excelStyles, props?.gridOptions]
  );

  const autoGroupColumnDef: ColDef = useMemo(
    () => ({
      width: 200,
      sortable: true,
      pinned: 'left',
      ...(props?.autoGroupColumnDef ?? {}),
    }),
    [props?.autoGroupColumnDef]
  );

  const defaultExcelExportParams = useMemo(() => {
    const customParams = getExportParams(props.defaultExcelExportParams as ExcelExportParams);
    return {
      processCellCallback: commonProcessCb,
      ...customParams,
    } as ExcelExportParams;
  }, [props.defaultExcelExportParams]);

  const defaultCsvExportParams = useMemo(() => {
    const customParams = getExportParams(props.defaultCsvExportParams);
    return {
      processCellCallback: commonProcessCb,
      ...customParams,
    } as CsvExportParams;
  }, [props.defaultCsvExportParams]);

  return (
    <div data-testid='ag-table' css={GridWrapper}>
      <AgGridReact
        animateRows
        ref={ref}
        suppressDragLeaveHidesColumns
        theme={props.theme ?? agForesightTheme}
        {...props}
        rowGroupPanelShow={props.rowGroupPanelShow ?? 'always'}
        loadingOverlayComponent={props.loadingOverlayComponent ?? GridLoadingOverlay}
        autoGroupColumnDef={autoGroupColumnDef}
        defaultColDef={defaultColDef}
        defaultCsvExportParams={defaultCsvExportParams}
        defaultExcelExportParams={defaultExcelExportParams}
        gridOptions={gridOptions}
        loadThemeGoogleFonts={false}
        onBodyScroll={onBodyScroll}
        onContextMenuVisibleChanged={onContextMenuVisibleChanged}
        onFilterChanged={onFilterChanged}
        onGridReady={onGridReady}
      />
    </div>
  );
});

export const GridWrapper = css`
  margin: 0;
  height: 100%;
  width: 100%;

  .ag-root-wrapper {
    background: transparent;
    .ag-row.ag-row-footer {
      background-color: ${colors.secondary[5]};
      box-shadow: ${`0 -0.1rem ${colors.neutral[20]}`};
    }
  }
  --ag-header-column-resize-handle-width: 1px;
  --ag-header-column-resize-handle-height: 100%;

  .ag-row-group:has(img, svg, .MuiAvatar-root, .MuiChip-root, .list-with-more-chip) {
    align-items: center;
  }
  .ag-header-cell-text {
    word-break: keep-all;
  }
  /* Honor newlines in multi-line header/cell tooltip strings
     (e.g. name / description / source / formula). No effect on
     single-line tooltips. */
  .ag-tooltip {
    white-space: pre-line;
  }
  .ag-row-group-contracted {
    background-color: #f8f8ff;
  }
  .ag-row-group.ag-row-group-expanded[class*='ag-row-level-'] {
    background-color: #f5f7ff;
    /* background-color: #f5f7ff; */
  }
  .ag-row-group.ag-row-group-expanded.ag-row-level-0 {
    background-color: #e7e8ff;
  }

  & a {
    color: ${colors.primary[60]};
  }
`;

export const AgTable = memo(AgTableForwarded);
